import {FormattableCurrency, toNativeCurrencyString} from '@ttx/core';
import {useEffect, useState} from 'react';
import {WalletNetwork} from '../../contexts/authentication-context/types';
import {useSmartContractContext} from '../../contexts/smart-contract/context';
import {Listing} from '../../contexts/smart-contract/types';
import {isWeb3Contract} from '../../contexts/smart-contract/utils';

interface ListingWithChainData extends Listing {
  currencySymbol: FormattableCurrency | null;
  price: string | null;
  isSold: boolean | null;
}
interface ListingWithLoading extends ListingWithChainData {
  loading: boolean;
}

export const useMarketplaceContract: ({
  tokenId,
  network,
  multiCopy,
  contractAddress,
}: {
  tokenId?: number;
  network?: WalletNetwork;
  multiCopy?: boolean;
  contractAddress?: string;
}) => ListingWithLoading = ({tokenId, network, multiCopy, contractAddress}) => {
  const [updatedListing, setUpdatedListing] = useState<
    ListingWithChainData | {price: undefined | null}
  >({price: null});

  const [loading, setLoading] = useState(true);

  const {marketplaceContract, getCurrencySymbolFromAddress} =
    useSmartContractContext();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (
          (!tokenId && tokenId !== 0) ||
          !network ||
          !contractAddress ||
          tokenId === -1
        ) {
          return;
        }

        const loadedMarketplaceContract = await marketplaceContract(network);

        if (!loadedMarketplaceContract) return;

        if (!isWeb3Contract(loadedMarketplaceContract)) return;

        const listing = await loadedMarketplaceContract
          .getTokenListing(contractAddress, tokenId)
          .then((data) => data[0])
          .catch((e) => console.error(e));

        if (!listing) {
          setUpdatedListing({price: undefined});
          return;
        }

        const currencySymbol = getCurrencySymbolFromAddress(
          listing.currency,
          network,
        );

        const nativePrice = toNativeCurrencyString(
          listing.buyoutPrice,
          currencySymbol,
        );

        // Pass this to the NFTItem component to indicate that the NFT is sold.
        const hasQuantity = listing
          ? // 0 quantity means it's sold.
            !listing.quantity.toNumber()
          : // null means we don't know if it is sold or not
            null;

        setUpdatedListing({
          ...listing,
          price: nativePrice !== '0' ? nativePrice : null,
          isSold: tokenId === 98 || hasQuantity,
          currencySymbol,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();

    return () => setUpdatedListing({price: null});
  }, [
    contractAddress,
    getCurrencySymbolFromAddress,
    marketplaceContract,
    network,
    tokenId,
  ]);

  return {...(updatedListing as ListingWithChainData), loading};
};
